import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDeleteMembershipInvitation } from 'src/features/membershipCloudUsers/api/useDeleteMembershipInvitation';
import { LIST_INVITATIONS_RESOURCE } from 'src/features/users/api/useListInvitations';
import { userMessages } from 'src/features/users/utils/messages';
import { Invitation } from 'src/features/users/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface DeleteMembershipInvitationProps<TData> extends DataTableNewEntryProps<TData> {
    selectedInvitation: Invitation;
    membershipId: string;
    children?: React.ReactNode;
}

export default function DeleteMembershipInvitation<TData>({
    selectedInvitation,
    membershipId,
    semantics,
    children,
}: DeleteMembershipInvitationProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deleteUser, isPending: isPendingDelete } = useDeleteMembershipInvitation({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_INVITATIONS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(userMessages.deleteInvitationSuccess),
                description: formatMessage(userMessages.deleteInvitationSuccessDetails, {
                    email: selectedInvitation.email,
                }),
            });
        },
        onError: (error) => {
            const errors = error.response?.data?.errors;
            const title = formatMessage(generalErrorMessages.somethingWentWrong);
            const description = errors ? errors[0]?.detail : formatMessage(generalErrorMessages.unknownError);

            toast({
                title,
                description,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteUser({ invitationId: selectedInvitation.id, membershipId, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={userMessages.deleteInvitationFor.id}
                                    defaultMessage={userMessages.deleteInvitationFor.defaultMessage}
                                    values={{
                                        email: selectedInvitation.email,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={userMessages.confirmDeleteInvitation.id}
                            defaultMessage={userMessages.confirmDeleteInvitation.defaultMessage}
                            values={{
                                email: selectedInvitation.email,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
